import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useDrawerStore, type DrawerView } from '@/stores/drawerStore';
import { AuthSelectView } from './views/AuthSelectView';
import { UrlInputFormView } from './views/UrlInputFormView';
import { DiagnosticFormView } from './views/DiagnosticFormView';
import { CompletedView } from './views/CompletedView';

/**
 * HalfModal
 * 右からスライドインする半モーダル
 * Zustand drawerStore で状態を管理
 * アクセシビリティ対応（ARIA属性、フォーカストラップ、Escapeキー対応）
 */
export function HalfModal() {
  const { activeView, closeDrawer } = useDrawerStore();
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // フォーカストラップとキーボードナビゲーション
  useEffect(() => {
    if (!activeView) return;

    const modalElement = modalRef.current;
    if (!modalElement) return;

    // 閉じるボタンにフォーカス
    closeButtonRef.current?.focus();

    // Tab キーのトラップ
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape キーでモーダルを閉じる
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }

      // Tab キーのトラップ処理
      if (e.key === 'Tab') {
        const focusableElements = modalElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (e.shiftKey) {
          // Shift+Tab: 最初の要素から前に移動しようとしたら最後へ
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          // Tab: 最後の要素から次に移動しようとしたら最初へ
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // body のスクロールを無効化
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [activeView, closeDrawer]);

  // モーダルが閉じている場合は何も表示しない
  if (!activeView) {
    return null;
  }

  // オーバーレイクリックでモーダルを閉じる
  const handleOverlayClick = () => {
    closeDrawer();
  };

  // モーダル内クリックの伝播を止める
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-end"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="half-modal-title"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Half Modal - Side Panel */}
      <div
        ref={modalRef}
        className="relative bg-white h-full w-full sm:w-[85%] md:w-[60%] lg:w-[45%] xl:w-[40%] shadow-2xl flex flex-col animate-slide-in-right"
        onClick={handleModalClick}
        role="document"
      >
        {/* Close Button */}
        <button
          ref={closeButtonRef}
          onClick={closeDrawer}
          className="absolute top-6 right-6 text-[#6B7280] hover:text-[#111827] transition-colors z-10"
          aria-label="モーダルを閉じる"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content based on activeView */}
        <HalfModalContent view={activeView} />
      </div>
    </div>
  );
}

/**
 * HalfModal のコンテンツを activeView に応じてレンダリング
 */
function HalfModalContent({ view }: { view: DrawerView }) {
  switch (view) {
    case 'auth':
      return <AuthSelectView />;
    case 'form':
      return <UrlInputFormView />;
    case 'diagnostic':
      return <DiagnosticFormView />;
    case 'completed':
      return <CompletedView />;
    default:
      return null;
  }
}
